import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { getCartClient } from "@/lib/client/api/cart"
import { useAuthStore } from "@/lib/store/auth-store"
import { RestaurantCart } from "@/lib/types/cart"
import { clearCart as clearCartAction } from "@/app/actions/cart"
import { addRestaurantItems } from "@/app/actions/order"
import { Customize as OrderCustomize, CustomizeOption as OrderCustomizeOption } from "@/lib/api/order-service"

export const useCartDetail = () => {
  return useQuery({
    queryKey: ["cart"],
    queryFn: getCartClient,
    staleTime: 30000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: true,
  })
}

// Hook to get item quantities for a specific restaurant
export const useCartItems = (restaurantId: string | number) => {
  const { data: cartResponse, isLoading } = useCartDetail()

  // Find the cart for this restaurant
  const restaurantCart = cartResponse?.carts?.find(
    (cart) => cart.restaurant_id === Number(restaurantId)
  )

  // Get item quantities
  const itemQuantities = new Map<number, number>()

  if (restaurantCart) {
    restaurantCart.restaurant_items.forEach((item) => {
      itemQuantities.set(item.item_id, item.quantity)
    })
  }

  // Return a function to get quantity for a specific item
  const getItemQuantity = (itemId: number): number => {
    return itemQuantities.get(itemId) || 0
  }

  return {
    getItemQuantity,
    isLoading,
    restaurantCart,
  }
}

export const useCartActions = () => {
  // Local state for cart actions
  const [isEditing, setIsEditing] = useState(false)
  const [selectedCarts, setSelectedCarts] = useState<number[]>([])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [cartsToDelete, setCartsToDelete] = useState<number[]>([])

  const queryClient = useQueryClient()
  const userId = useAuthStore((state) => state.user?.user_id)
  const { data: cartResponse } = useCartDetail()

  // Delete cart mutation
  const deleteMutation = useMutation({
    mutationFn: async (cartIds: number[]) => {
      if (!userId) {
        throw new Error("User not authenticated")
      }

      // Convert cart IDs to strings before calling the action
      return clearCartAction(
        userId.toString(),
        cartIds.map((id) => id.toString())
      )
    },
    onSuccess: () => {
      toast.success("Đã xóa giỏ hàng", {
        description: "Giỏ hàng đã được xóa thành công",
      })

      // Invalidate cart query to refresh data
      queryClient.invalidateQueries({ queryKey: ["cart"] })
      setIsEditing(false)
      setSelectedCarts([])
      setDeleteDialogOpen(false)
    },
    onError: () => {
      toast.error("Không thể xóa giỏ hàng", {
        description: "Đã xảy ra lỗi khi xóa giỏ hàng. Vui lòng thử lại sau.",
      })
    },
  })

  // Update item quantity mutation
  const updateItemQuantityMutation = useMutation({
    mutationFn: async ({
      restaurantId,
      itemId,
      newQuantity,
      cartId,
    }: {
      restaurantId: number | string;
      itemId: number;
      newQuantity: number;
      cartId?: string | number;
    }) => {
      if (!userId) {
        throw new Error("User not authenticated")
      }

      // Get the current cart
      const currentCart = cartResponse?.carts?.find(
        (cart) => cart.restaurant_id === Number(restaurantId)
      )

      if (!currentCart) {
        throw new Error("Cart not found")
      }

      // Prepare all cart items with the updated quantity
      const updatedItems = currentCart.restaurant_items.map((item) => {
        // Convert customizations to the required format for the API
        const customizes: OrderCustomize[] = (item.customizes || []).map(customize => ({
          id: customize.id.toString(),
          name: customize.name,
          options: customize.options.map(option => ({
            id: option.id.toString(),
            name: option.name,
            price: option.price,
            quantity: option.quantity
          }))
        }))

        if (item.item_id === itemId) {
          return {
            item_id: item.item_id.toString(),
            quantity: newQuantity,
            price: item.price,
            note: item.note || "",
            customizes: customizes,
          }
        }
        return {
          item_id: item.item_id.toString(),
          quantity: item.quantity,
          price: item.price,
          note: item.note || "",
          customizes: customizes,
        }
      }).filter(item => item.quantity > 0) // Remove items with zero quantity

      // Call the API to update the cart
      return addRestaurantItems(
        userId.toString(),
        restaurantId.toString(),
        updatedItems,
        {
          latitude: 0,
          longitude: 0,
          cartId: cartId?.toString() || currentCart.cart_id?.toString(),
        }
      )
    },
    onSuccess: () => {
      // Invalidate cart query to refresh data
      queryClient.invalidateQueries({ queryKey: ["cart"] })
    },
    onError: (error) => {
      toast.error("Không thể cập nhật số lượng", {
        description: 
          error instanceof Error
            ? error.message
            : "Đã xảy ra lỗi khi cập nhật số lượng. Vui lòng thử lại sau.",
      })
    },
  })

  // Toggle edit mode
  const toggleEditMode = () => {
    if (isEditing) {
      setIsEditing(false)
      setSelectedCarts([])
    } else {
      setIsEditing(true)
    }
  }

  // Toggle selected cart
  const toggleCartSelection = (cartId: number) => {
    setSelectedCarts((prev) =>
      prev.includes(cartId)
        ? prev.filter((id) => id !== cartId)
        : [...prev, cartId]
    )
  }

  // Select all carts
  const toggleSelectAll = () => {
    if (!cartResponse?.carts) return

    if (selectedCarts.length === cartResponse.carts.length) {
      setSelectedCarts([])
    } else {
      setSelectedCarts(cartResponse.carts.map((cart) => cart.cart_id))
    }
  }

  // Handle delete confirmation
  const confirmDelete = (cartIds: number[]) => {
    setCartsToDelete(cartIds)
    setDeleteDialogOpen(true)
  }

  // Execute delete
  const executeDelete = () => {
    deleteMutation.mutate(cartsToDelete)
  }

  // Cancel delete
  const cancelDelete = () => {
    setDeleteDialogOpen(false)
    setCartsToDelete([])
  }

  // Find cart by ID
  const getCartById = (cartId: number): RestaurantCart | undefined => {
    return cartResponse?.carts?.find((cart) => cart.cart_id === cartId)
  }

  // Function to update item quantity
  const updateItemQuantity = (
    restaurantId: number | string,
    itemId: number,
    newQuantity: number
  ) => {
    const currentCart = cartResponse?.carts?.find(
      (cart) => cart.restaurant_id === Number(restaurantId)
    )

    updateItemQuantityMutation.mutate({
      restaurantId,
      itemId,
      newQuantity,
      cartId: currentCart?.cart_id,
    })
  }

  return {
    // State
    isEditing,
    selectedCarts,
    deleteDialogOpen,
    cartsToDelete,

    // Actions
    toggleEditMode,
    toggleCartSelection,
    toggleSelectAll,
    confirmDelete,
    executeDelete,
    cancelDelete,
    getCartById,
    updateItemQuantity,

    // Mutations
    deleteMutation,
  }
}
